import { Link } from 'react-router-dom';

import { AuthError } from '@/modules/auth/components/AuthError';
import { LinkedInLogo } from '@/modules/auth/components/LinkedInLogo';
import { AuthLayout } from '@/modules/layouts/components/authLayout/AuthLayout';
import { DocumentMetadata } from '@/modules/layouts/components/DocumentMetadata';
import { Button } from '@/modules/ui/components/button/Button';
import { ProviderMark } from '@/pages/login/providerMark/ProviderMark';

export interface LinkedInCallbackError {
  message: string;
  /** Sign-up link that keeps a still-valid trial offer code. */
  signUpPath: string;
}

interface LinkedInCallbackStatusProps {
  /** Absent while the LinkedIn sign-in is still being exchanged. */
  error?: LinkedInCallbackError;
}

const logo = <LinkedInLogo className="size-7 text-[#0A66C2]" />;

/** What `/auth/callback/linkedin` shows while signing in or after it fails. */
export const LinkedInCallbackStatus = ({
  error,
}: LinkedInCallbackStatusProps) => {
  if (!error) {
    return (
      <>
        <DocumentMetadata title="Signing you in" />
        <AuthLayout
          mark={<ProviderMark provider="LinkedIn" logo={logo} />}
          heading="Signing you in"
          subheading="Finishing your LinkedIn sign-in. This usually takes a few seconds."
        />
      </>
    );
  }

  return (
    <AuthLayout
      mark={<ProviderMark provider="LinkedIn" logo={logo} failed />}
      heading="LinkedIn sign-in failed"
      subheading="We couldn’t sign you in with LinkedIn. Go back and try again, or use another sign-in method."
      switchLink={{
        prompt: 'New to Finlay?',
        action: 'Create an account',
        to: error.signUpPath,
      }}
    >
      <DocumentMetadata title="LinkedIn sign-in failed" />
      <AuthError message={error.message} />
      <Button
        variant="primary"
        className="h-9 w-full rounded-md active:scale-[0.98]"
        asChild
      >
        <Link to="/login">Back to sign in</Link>
      </Button>
    </AuthLayout>
  );
};
